import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatJPY, formatDate } from "@/lib/format";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Check, X, Loader2, Link2, FileCheck2 } from "lucide-react";
import { toast } from "sonner";

const EVENT_LABEL = { invoice_issue: "売上計上（請求確定）", payment: "入金消込", refund: "返金" };
const STATUS_LABEL = { pending: "承認待ち", posted: "確定済み", rejected: "却下" };

function LinkCard({ rec, onApprove, onReject, busy }) {
  const c = rec.candidate;
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-4" data-testid={`link-card-${rec.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-indigo-50 text-indigo-700 px-2.5 py-0.5 text-xs font-medium">{EVENT_LABEL[rec.event] || rec.event}</span>
            <span className="text-xs text-stone-400">{formatDate(c.date)}</span>
          </div>
          <div className="mt-1 text-sm font-medium text-stone-900">{c.description}</div>
        </div>
        {rec.status === "pending" && (
          <div className="flex items-center gap-2">
            <button onClick={() => onReject(rec)} disabled={busy} className="inline-flex items-center gap-1 rounded-md border border-stone-200 px-3 py-1.5 text-xs font-medium text-stone-600 hover:bg-stone-50" data-testid={`reject-${rec.id}`}><X className="h-3.5 w-3.5" /> 却下</button>
            <button onClick={() => onApprove(rec)} disabled={busy} className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700" data-testid={`approve-${rec.id}`}><Check className="h-3.5 w-3.5" /> 承認して記帳</button>
          </div>
        )}
        {rec.status === "posted" && <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-medium"><FileCheck2 className="h-4 w-4" /> 記帳済み</span>}
        {rec.status === "rejected" && <span className="text-stone-400 text-xs">却下</span>}
      </div>
      <div className="mt-3 rounded-lg border border-stone-100 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-stone-400 text-[11px]"><tr><th className="px-3 py-1.5 text-left">勘定科目</th><th className="px-3 py-1.5 text-right">借方</th><th className="px-3 py-1.5 text-right">貸方</th></tr></thead>
          <tbody className="divide-y divide-stone-100">
            {c.lines.map((l, i) => (
              <tr key={i}>
                <td className="px-3 py-1.5 text-stone-700">{l.account}</td>
                <td className="px-3 py-1.5 text-right">{l.debit ? formatJPY(l.debit) : ""}</td>
                <td className="px-3 py-1.5 text-right">{l.credit ? formatJPY(l.credit) : ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function AccountingLink() {
  const [recs, setRecs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending");
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/billing/accounting-links").then((r) => setRecs(r.data || [])).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const approve = async (rec) => {
    setBusy(true);
    try { await api.post(`/billing/accounting-links/${rec.id}/approve`); toast.success("会計帳簿へ確定登録しました"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const reject = async (rec) => {
    setBusy(true);
    try { await api.post(`/billing/accounting-links/${rec.id}/reject`); toast.success("却下しました"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const filtered = recs.filter((r) => r.status === tab);

  return (
    <div data-testid="billing-accounting-link-page">
      <div className="mb-6">
        <h1 className="font-display font-bold text-2xl text-stone-900 flex items-center gap-2"><Link2 className="h-6 w-6 text-indigo-600" /> 会計連携</h1>
        <p className="text-sm text-stone-500 mt-0.5">請求・入金・返金から生成された仕訳候補を承認し、会計帳簿へ確定登録します。確定仕訳は変更されません。</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="link-tabs">
          <TabsTrigger value="pending">承認待ち（{recs.filter((r) => r.status === "pending").length}）</TabsTrigger>
          <TabsTrigger value="posted">記帳済み</TabsTrigger>
          <TabsTrigger value="rejected">却下</TabsTrigger>
        </TabsList>
        <TabsContent value={tab} className="mt-4 space-y-3">
          {loading ? (
            <div className="py-16 text-center text-stone-400"><Loader2 className="h-6 w-6 animate-spin inline" /></div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center text-stone-400">{STATUS_LABEL[tab]}の仕訳はありません</div>
          ) : filtered.map((rec) => (
            <LinkCard key={rec.id} rec={rec} onApprove={approve} onReject={reject} busy={busy} />
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
